import React from 'react';
import {
  Box, Link, Stack, Typography,
} from '@mui/material';
import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

const inlineCodeSx = {
  typography: 'code',
  backgroundColor: 'neutral.main',
  px: 0.5,
  py: 0.5,
  borderRadius: 0.5,
};

const headingSx = { '&:not(:first-child)': { pt: 2 } };

// Please do not change this, took some experimenting time to get the output look nice
// If changing, make sure that number and regular bulleting looks correct in chat output. / Calle
const listSx = {
  m: 0,
  pl: 1,
  listStyle: 'none',
  '& > li': {
    display: 'flex',
    gap: 0.75,
  },
  '& > li::before': {
    flex: '0 0 1.25em',
  },
};

const unorderedListSx = {
  ...listSx,
  '& > li::before': {
    ...listSx['& > li::before'],
    content: "'\\2022'",
  },
};

const getOrderedListSx = (start?: number) => ({
  ...listSx,
  counterReset: `list-item ${Math.max(0, (start || 1) - 1)}`,
  '& > li': {
    ...listSx['& > li'],
    counterIncrement: 'list-item',
  },
  '& > li::before': {
    ...listSx['& > li::before'],
    content: "counter(list-item) '.'",
    textAlign: 'right',
  },
});

// Assistant replies are model-authored markdown; these map the elements
// react-markdown emits onto the MUI typography the rest of the panel uses.
const markdownComponents: Components = {
  // Models use single newlines as real breaks, which markdown would collapse.
  p: ({ children }) => (
    <Typography variant="reading" component="p" sx={{ whiteSpace: 'pre-wrap', textWrap: 'pretty' }}>{children}</Typography>
  ),
  h1: ({ children }) => (
    <Typography variant="largeTitle" sx={headingSx}>{children}</Typography>
  ),
  h2: ({ children }) => (
    <Typography variant="largeTitle" sx={headingSx}>{children}</Typography>
  ),
  h3: ({ children }) => (
    <Typography variant="mediumTitle" sx={headingSx}>{children}</Typography>
  ),
  h4: ({ children }) => (
    <Typography variant="mediumTitle" sx={headingSx}>{children}</Typography>
  ),
  h5: ({ children }) => (
    <Typography variant="mediumTitle" sx={headingSx}>{children}</Typography>
  ),
  h6: ({ children }) => (
    <Typography variant="mediumTitle" sx={headingSx}>{children}</Typography>
  ),
  ul: ({ children }) => <Box component="ul" sx={unorderedListSx}>{children}</Box>,
  ol: ({ children, start }) => <Box component="ol" start={start} sx={getOrderedListSx(start)}>{children}</Box>,
  li: ({ children }) => (
    <Box component="li">
      <Typography variant="reading" component="span" sx={{ textWrap: 'pretty' }}>{children}</Typography>
    </Box>
  ),
  strong: ({ children }) => <Box component="strong" sx={{ fontWeight: 'fontWeightBold' }}>{children}</Box>,
  code: ({ children }) => <Box component="code" sx={inlineCodeSx}>{children}</Box>,
  // Fenced code arrives as <pre><code>, so the block styling lives here and the
  // nested rule strips the inline treatment off the code element inside it.
  pre: ({ children }) => (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 3,
        borderRadius: 1.5,
        overflowX: 'auto',
        backgroundColor: 'neutral.main',
        typography: 'code',
        '& code': { all: 'unset' },
      }}
    >
      {children}
    </Box>
  ),
  a: ({ children, href }) => (
    <Link href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </Link>
  ),
  table: ({ children }) => (
    <Box sx={{ overflowX: 'auto' }}>
      <Box
        component="table"
        sx={{
          borderCollapse: 'collapse',
          width: 'max-content',
          minWidth: '100%',
          '& th, & td': {
            border: 1,
            borderColor: 'divider',
            px: 2,
            py: 1,
            textAlign: 'left',
            verticalAlign: 'top',
          },
          '& th': {
            backgroundColor: 'neutral.main',
            fontWeight: 'fontWeightBold',
            whiteSpace: 'nowrap',
          },
        }}
      >
        {children}
      </Box>
    </Box>
  ),
};

const MarkdownMessage = ({ content }: { content: string }) => (
  <Stack spacing={2}>
    <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{content}</Markdown>
  </Stack>
);
export default MarkdownMessage;
